import { componentText, SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import { css, html } from 'lit'
import { property, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { live } from 'lit/directives/live.js'
import { when } from 'lit/directives/when.js'
import { formatDateInstant, toLocalWallClock } from '../date-format'
import dayjs from 'dayjs'
// Registering side-effect imports — this panel composes every tag below, so it
// must not depend on a consumer's barrel import order to upgrade them.
import '@schmancy/primitives/button/button'
import '@schmancy/primitives/calendar/calendar'
import '@schmancy/primitives/icons/icon'
import '@schmancy/primitives/surface/surface'
import '@schmancy/primitives/time-picker/time-picker'
import '@schmancy/primitives/typography/typography'
import '@schmancy/form/fields/typography'
import '@schmancy/form/fields/input'
import type { SchmancyCalendarChangeEvent } from '@schmancy/primitives/calendar/calendar'

/**
 * Picker panel for `<schmancy-date-field>` — one surface holding a DRAFT, and
 * committing once.
 *
 *   draft bar   localized instant, direct date entry, editable time, Now / Today
 *   calendar    <schmancy-calendar mode="single">, the ONLY scroller here
 *   time wheels <schmancy-time-picker>, datetime-local only
 *   action bar  the refusal and its one-tap repair, then Cancel / Save
 *
 * Nothing the user touches leaves this element until Save. The draft bar and the
 * action bar are fixed flex rows rather than `sticky bottom-0`: they cannot
 * scroll because nothing around them does, which needs no indefinite-height
 * percentage chain to hold.
 *
 * Values are naive local wall-clock strings (`YYYY-MM-DD`, `YYYY-MM-DDTHH:mm`) —
 * zero-padded, so `<` and `>` order them chronologically and every bound verdict
 * below agrees with the field's own dayjs `checkValidity` with no parser between
 * them.
 *
 * @element schmancy-date-field-dialog
 * @fires calendar-input  - Every draft move — day, time, preset   { value }
 * @fires calendar-change - Save only                              { value }
 * @fires calendar-cancel - Cancel only                            —
 * @fires close           - Save and Cancel, after the above
 */
@schmancyElement('schmancy-date-field-dialog')
export class SchmancyDateFieldDialog extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
			}
		`,
	]

	@property() type: 'date' | 'datetime-local' = 'date'
	@property() value: string | null = null
	@property() min?: string
	@property() max?: string

	/**
	 * The user's uncommitted edit. `null` means untouched, which is what makes the
	 * incoming `value` the draft's opening position without a lifecycle hook
	 * racing the property binding that delivers it.
	 */
	@state() private edit: string | null = null
	@state() private dateEntry: string | null = null

	/**
	 * What `Now` means, captured once when this panel is constructed rather than
	 * read at render: a target that moved between two renders would make the same
	 * button mean two things inside one session.
	 */
	readonly #openedAt = new Date()

	/** Every draft move goes through here, so `calendar-input` reports all of
	 *  them — day, time, typed readout, preset, one-tap repair — and nothing can
	 *  move the draft without saying so. */
	#draftTo(next: string): void {
		this.edit = next
		this.dateEntry = null
		this.dispatchEvent(new CustomEvent('calendar-input', { detail: { value: next }, bubbles: true, composed: true }))
	}

	/** A structured close: it bypasses `allowsDismiss` and keeps the exit
	 *  animation, where reaching back through the field's own teardown would snap
	 *  the panel out un-animated. */
	#close(): void {
		this.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }))
	}

	render() {
		const isDatetime = this.type === 'datetime-local'
		const locale = this.locale
		const draft = this.edit ?? this.value ?? ''
		const day = draft.slice(0, 10)
		const enteredDay = this.dateEntry ?? day
		const invalidDate =
			this.dateEntry !== null &&
			(!/^\d{4}-\d{2}-\d{2}$/.test(enteredDay) ||
				!dayjs(enteredDay).isValid() ||
				dayjs(enteredDay).format('YYYY-MM-DD') !== enteredDay)
		// The whole tail, not a fixed 16: `<schmancy-time-picker seconds>` carries
		// `HH:mm:ss`, and a hardcoded cut silently truncates it. Nothing here turns
		// that column on, and everything here passes the tail through whole so
		// nothing has to change when something does.
		const time = draft.length > 10 ? draft.slice(11) : ''
		const clock = toLocalWallClock(this.#openedAt, 'datetime-local')
		// `Now` is the one-tap escape from a window a seeded value already sits
		// outside of, so it is clamped into that window and is therefore always
		// legal. A user who dawdles past a `max` frozen at the consumer's last
		// render gets the latest instant it admits, which is the honest answer.
		const preset = clampInto(isDatetime ? clock : clock.slice(0, 10), this.min, this.max)
		const under = !!draft && !!this.min && draft < this.min
		const over = !!draft && !!this.max && draft > this.max
		const bound = under ? (this.min ?? '') : over ? (this.max ?? '') : ''
		// BOTH bounds reach the picker as a time of day, each on its own day. A
		// panel that withheld `max` let the wheel produce a minute the field then
		// refused, and explained the refusal afterwards — offering an illegal value
		// and apologising for it, with the refusal row below as the apology.
		//
		// Withholding it was justified twice and neither holds. That a same-minute
		// window would leave the wheel nowhere to rest is what the slot rule
		// answers: the endpoints are admitted whatever the grid, so `[X, X]` offers
		// exactly `X`. And that a snapshot of a moving `max` would disagree with the
		// field is the wrong direction of disagreement — the panel freezes its
		// bounds at open, the field validates against its own live `max`, and time
		// only moves forward, so a frozen bound can only ever be the conservative
		// one the field still accepts.
		const timeOfDayOn = (bound: string | undefined): string | undefined =>
			bound && bound.length > 10 && day === bound.slice(0, 10) ? bound.slice(11) : undefined
		const timeMin = timeOfDayOn(this.min)
		const timeMax = timeOfDayOn(this.max)
		// The exact instant the repair sets — named by the button and written by
		// its click, so the label can never describe a value other than the one it
		// produces. A date-only bound reaching a datetime field carries no time, so
		// it takes the end of its own day that lies inside the window.
		const repair = isDatetime
			? bound.length > 10
				? bound
				: `${bound}T${under ? '00:00' : '23:59'}`
			: bound.slice(0, 10)

		return html`
			<!-- mx-auto, not stretch: the overlay mounts content into a
			     grid-template-columns: minmax(0,1fr) area, and a grid item whose
			     max-width stops it filling that area falls back from stretch to
			     start — so in any surface wider than 90 this panel pinned itself to
			     the left edge and left the rest of the surface empty. The auto
			     margins centre it in whatever box it lands in. -->
			<div class="flex flex-col h-full w-full min-w-70 max-w-90 mx-auto">
				<!-- THE DRAFT BAR. Frosted over the calendar it commands, exactly as
				     the action bar is — inside an overlay a fixed row floats on glass
				     rather than painting a page ground. The preset rides this line
				     because this line is what it changes. -->
				<schmancy-surface
					part="draft-bar"
					type="container"
					opacity="translucent"
					elevated
					class="shrink-0 z-20 rounded-[inherit] border-b border-outline-variant/40"
				>
					<div class="flex items-center gap-2 px-3 py-2">
						<div class="flex-1 min-w-0 flex items-baseline gap-2">
							<schmancy-typography type="title" token="md" class="truncate">
								${day ? formatDateInstant(day, 'date', '', locale) : componentText('dateField.placeholder')}
							</schmancy-typography>
							${when(
								isDatetime && !!day,
								() => html`
									<!-- The precision path. A tap puts a caret in the numerals and
									     the box never moves; the wheels below are the thumb path for
									     the same value. -->
									<schmancy-typography-field
										type="title"
										token="md"
										inputmode="numeric"
										class="w-14 tabular-nums"
										.label=${componentText('dateField.timeOfDay')}
										placeholder="--:--"
										.value=${live(time.slice(0, 5))}
										@change=${(e: CustomEvent<{ value: string }>) => {
											e.stopPropagation()
											const digits = e.detail.value.replace(/\D/g, '').slice(0, 4)
											if (digits.length < 3) return
											const hh = String(Math.min(23, Number(digits.slice(0, digits.length - 2)))).padStart(2, '0')
											const mm = String(Math.min(59, Number(digits.slice(-2)))).padStart(2, '0')
											// The tail rides along: this readout takes hours and minutes, and a
											// seconds segment the wheel below may carry is not its to drop.
											this.#draftTo(`${day}T${hh}:${mm}${time.slice(5)}`)
										}}
									></schmancy-typography-field>
								`,
							)}
						</div>
						<!-- autofocus, not a focus() call from this panel: the overlay moves
						     initial focus itself on open, and a call from here would race that
						     pass and lose. Its fallback takes the first focusable descendant,
						     which is the time readout above — so a panel opened over a value
						     already set put a caret in numerals nobody came to retype. -->
						<schmancy-button autofocus variant="tonal" size="sm" @click=${() => this.#draftTo(preset)}>
							${componentText(isDatetime ? 'dateField.preset.now' : 'dateField.preset.today')}
						</schmancy-button>
					</div>
					<div class="px-3 pb-2">
						<schmancy-input
							part="date-entry"
							size="sm"
							.label=${componentText('dateField.dateEntry')}
							placeholder=${componentText('dateField.dateEntryPlaceholder')}
							.value=${live(enteredDay)}
							.error=${invalidDate}
							.validationMessage=${invalidDate ? componentText('dateField.invalidDate') : ''}
							@input=${(e: CustomEvent<{ value: string }>) => {
								e.stopPropagation()
								this.dateEntry = e.detail.value
								if (
									!/^\d{4}-\d{2}-\d{2}$/.test(e.detail.value) ||
									!dayjs(e.detail.value).isValid() ||
									dayjs(e.detail.value).format('YYYY-MM-DD') !== e.detail.value
								)
									return
								this.#draftTo(isDatetime ? `${e.detail.value}T${time || clock.slice(11, 16)}` : e.detail.value)
							}}
						></schmancy-input>
					</div>
				</schmancy-surface>

				<!-- THE ONLY SCROLLER, and now one element rather than two. The
				     calendar already owns a scrollport of its own inside a host that
				     declares its height; wrapping it in a second overflow-y-auto with a
				     max-h-80 of its own made two elements each answer "how tall is the
				     calendar" — 352 and 320 — and the outer one then clipped 32px off
				     the inner one it could not see. Measured live at 1280x900: scroller
				     320, content 352, overflow 32, in BOTH modes.
				     The cap belongs on the calendar itself, where it clamps the one
				     height there is: flex-1 and min-h-0 make it absorb what the surface
				     gives, max-h-80 keeps it from taking the panel out of a clamped
				     popover, and its own scrollport then measures exactly that.
				     The calendar disables a day only when no instant within it falls in
				     [min, max] (its own day-vs-instant comparison), so the full
				     min/max — time-of-day included — passes through unmodified. -->
				<schmancy-calendar
					part="calendar"
					class="flex-1 min-h-0 max-h-80"
					mode="single"
					.type=${this.type}
					.value=${draft || null}
					min=${ifDefined(this.min)}
					max=${ifDefined(this.max)}
					@change=${(e: SchmancyCalendarChangeEvent) => {
						e.stopPropagation()
						if (!e.detail.value) return
						const picked = e.detail.value.slice(0, 10)
						if (!isDatetime) {
							this.#draftTo(picked)
							return
						}
						// A day arriving while a time is already drafted keeps that time. A
						// day arriving with none takes the clock's own, to the minute,
						// clamped into the window — midnight is never the answer anyone
						// means, and a midnight default is exactly what drops a fresh draft
						// outside an [order sent, now] window on its very first frame. No
						// snapping to a grid here: the panel does not decide the wheel's
						// step, and the wheel shows whatever it is handed.
						this.#draftTo(
							time ? `${picked}T${time}` : clampInto(`${picked}T${clock.slice(11, 16)}`, this.min, this.max),
						)
					}}
				></schmancy-calendar>

				${when(
					isDatetime,
					() => html`
						<!-- THE TIME WHEELS. Always rendered, so the panel never changes
						     height under the user's thumb; inert until a day exists, because
						     a time with no day is not a value. Both bounds reach them, each
						     on its own day, so the last reachable minute IS the latest legal
						     instant rather than the last grid step before it.

						     No minuteStep either. The picker's own default is the most
						     granular grid there is, and a panel that overrode it would be
						     choosing a coarseness for all four consumers — which is how a
						     delivery seeded at 14:14 came back 14:10 before anyone had
						     touched it. A consumer wanting 5 or 15 says so on the field. -->
						<!-- No vertical padding here. The wheel is three cells deep and only
						     the middle one carries the time, so at rest it already spends a
						     whole cell of empty above the numerals and another below;
						     padding outside it was air stacked on air, and 16px of the 149
						     this section cost the panel. The rule stays — it is what
						     separates the wheels from the grid — and the wheel's own lead
						     spacer is the gap beneath it. -->
						<div part="wheels" class="shrink-0 border-t border-outline-variant/40">
							<schmancy-time-picker
								.value=${time || null}
								min=${ifDefined(timeMin)}
								max=${ifDefined(timeMax)}
								?disabled=${!day}
								@change=${(e: CustomEvent<{ value: string }>) => {
									e.stopPropagation()
									if (!day) return
									this.#draftTo(`${day}T${e.detail.value}`)
								}}
							></schmancy-time-picker>
						</div>
					`,
				)}

				<!-- THE ACTION BAR. Present before the first touch and after it, on the
				     same glass as the draft bar. The refusal states the bound, and the
				     control beside it is the whole repair — one tap, without leaving —
				     so the reason sits in the same bar as the disabled control rather
				     than behind a press. -->
				<schmancy-surface
					part="actions"
					type="container"
					opacity="translucent"
					elevated
					class="shrink-0 z-10 rounded-[inherit] border-t border-outline-variant/40"
				>
					<!-- px-3 py-1.5, not p-3: at a phone's height this bar was charging
					     24px of vertical padding around a 48px control, and that height
					     belongs to the calendar and the wheels. The compaction is the
					     BAR's — the buttons keep their full touch target, because buying
					     padding back out of a thumb-sized control is not a saving. -->
					<div class="grid gap-1 px-3 py-1.5">
						<!-- THE REFUSAL, now a rarity rather than the house style. The wheels
						     carry both bounds, so this panel can no longer produce a time
						     its field refuses; what still reaches here is a value a consumer
						     SEEDED outside its own bounds, which is the one case worth a
						     row. No icon beside it: the row is already a role="alert" in
						     error ink stating the bound, with its repair beside it, so a
						     glyph reports nothing the ordering does not already carry —
						     and it reached the a11y tree as a nameless graphic besides. -->
						${when(
							!!bound,
							() => html`
								<div class="flex items-center gap-2" role="alert">
									<schmancy-typography type="body" token="sm" class="flex-1 min-w-0 text-error-default">
										${componentText(under ? 'dateField.outOfRange.tooEarly' : 'dateField.outOfRange.tooLate', {
											date: formatDateInstant(bound, this.type, '', locale),
										})}
									</schmancy-typography>
									<!-- The button NAMES the instant it sets, in the draft bar's own
									     format. "Use latest" named a category the reader had to
									     decode into a value; this says what happens. -->
									<schmancy-button variant="text" size="sm" @click=${() => this.#draftTo(repair)}>
										${componentText(under ? 'dateField.useEarliest' : 'dateField.useLatest', {
											date: formatDateInstant(repair, this.type, '', locale),
										})}
									</schmancy-button>
								</div>
							`,
						)}
						<div class="flex justify-end gap-2">
							<schmancy-button
								variant="text"
								@click=${() => {
									this.dispatchEvent(new CustomEvent('calendar-cancel', { bubbles: true, composed: true }))
									this.#close()
								}}
							>
								${componentText('dateField.cancel')}
							</schmancy-button>
							<schmancy-button
								variant="filled"
								?disabled=${!draft || !!bound || invalidDate}
								@click=${() => {
									this.dispatchEvent(
										new CustomEvent('calendar-change', { detail: { value: draft }, bubbles: true, composed: true }),
									)
									this.#close()
								}}
							>
								${componentText('dateField.save')}
							</schmancy-button>
						</div>
					</div>
				</schmancy-surface>
			</div>
		`
	}
}

/** Holds a naive local wall-clock string inside `[min, max]` by string order —
 *  which is chronological order for these zero-padded shapes, and is the same
 *  verdict the field's dayjs comparison reaches. */
function clampInto(value: string, min?: string, max?: string): string {
	if (min && value < min) return min
	if (max && value > max) return max
	return value
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-date-field-dialog': SchmancyDateFieldDialog
	}
}
